import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/Toast';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, Select } from '../common/Input';
import { Modal } from '../common/Modal';
import { apiError } from '../../utils/apiError';
import { Service } from '../../types';

interface Props {
  service: Service;
  /** The chart's current window, so "export what I'm looking at" is one click. */
  rangeHours: number;
}

type Dialog = 'clone' | 'template' | 'export' | null;

/**
 * The secondary actions on a service: clone, save as template, export raw
 * checks. A dropdown rather than three more buttons in the header.
 */
export function ServiceMoreMenu({ service, rangeHours }: Props) {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const [cloneName, setCloneName] = useState(`${service.name} (copy)`);
  const [clonePaused, setClonePaused] = useState(false);
  const clone = useMutation({
    mutationFn: () => api.cloneService(service.id, { name: cloneName.trim() || undefined, enabled: clonePaused ? false : undefined }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      addToast('success', `Created "${created.name}"`);
      setDialog(null);
      navigate(`/services/${created.id}/edit`);
    },
    onError: err => addToast('error', apiError(err, 'Could not clone the service')),
  });

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const template = useMutation({
    mutationFn: () => api.createServiceTemplate({ name: templateName.trim(), description: templateDescription.trim() || null, from_service_id: service.id }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['service-templates'] });
      addToast('success', `Saved template "${t.name}". Credentials were not copied.`);
      setDialog(null);
    },
    onError: err => addToast('error', apiError(err, 'Could not save the template')),
  });

  const [exportRange, setExportRange] = useState<string>(String(rangeHours));
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const exportHref = () => {
    const hours = parseInt(exportRange, 10);
    const from = new Date(Date.now() - hours * 3_600_000).toISOString();
    return api.checksExportUrl(service.id, { from, format: exportFormat });
  };

  const canCreate = hasPermission('services.create');
  const items = [
    ...(canCreate ? [{ key: 'clone', label: 'Clone…', onSelect: () => { setCloneName(`${service.name} (copy)`); setDialog('clone'); } }] : []),
    ...(canCreate ? [{ key: 'template', label: 'Save as template…', onSelect: () => { setTemplateName(''); setDialog('template'); } }] : []),
    { key: 'export', label: 'Export checks…', onSelect: () => { setExportRange(String(rangeHours)); setDialog('export'); } },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <Button variant="secondary" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}>
        More
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-48 py-1 rounded-md shadow-overlay bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-10">
          {items.map(item => (
            <button
              key={item.key}
              role="menuitem"
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-700"
              onClick={() => { setOpen(false); item.onSelect(); }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <Modal open={dialog === 'clone'} onClose={() => setDialog(null)} title="Clone service">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); clone.mutate(); }} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The copy has the same configuration, credentials, tags and groups, and starts with no history.
            {service.type === 'heartbeat' ? ' It gets its own push token.' : ''} You land on its edit page to change the target.
          </p>
          <Field label="Name" required>
            {p => <Input {...p} required maxLength={255} value={cloneName} onChange={e => setCloneName(e.target.value)} autoFocus />}
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={clonePaused} onChange={e => setClonePaused(e.target.checked)} />
            Start paused
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" loading={clone.isPending}>Clone</Button>
          </div>
        </form>
      </Modal>

      <Modal open={dialog === 'template'} onClose={() => setDialog(null)} title="Save as template">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); template.mutate(); }} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            A template pre-fills the “Add service” form. Credentials (auth value, client key, credential headers) are never stored in one.
          </p>
          <Field label="Template name" required>
            {p => <Input {...p} required maxLength={100} value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Standard HTTPS health check" autoFocus />}
          </Field>
          <Field label="Description">
            {p => <Input {...p} maxLength={500} value={templateDescription} onChange={e => setTemplateDescription(e.target.value)} />}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" loading={template.isPending} disabled={!templateName.trim()}>Save template</Button>
          </div>
        </form>
      </Modal>

      <Modal open={dialog === 'export'} onClose={() => setDialog(null)} title="Export checks">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">Every raw check in the range, oldest first — for audit or offline analysis.</p>
          <Field label="Range">
            {p => (
              <Select {...p} value={exportRange} onChange={e => setExportRange(e.target.value)}>
                <option value="1">Last hour</option>
                <option value="24">Last 24 hours</option>
                <option value="168">Last 7 days</option>
                <option value="720">Last 30 days</option>
                <option value="2160">Last 90 days</option>
              </Select>
            )}
          </Field>
          <Field label="Format">
            {p => (
              <Select {...p} value={exportFormat} onChange={e => setExportFormat(e.target.value as 'csv' | 'json')}>
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </Select>
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <a href={exportHref()} download onClick={() => setDialog(null)}>
              <Button type="button">Download</Button>
            </a>
          </div>
        </div>
      </Modal>
    </div>
  );
}
